import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { CreateKeyResult, Destination, NamingMode, UpdateDestinationInput } from "../lib/types";
import { NamingSelect } from "../components/NamingSelect";
import { StatusBadge, Badge } from "../components/StatusBadge";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState } from "../components/EmptyState";
import { OwnerCell } from "../components/OwnerCell";
import { formatBytes, formatRelative } from "../lib/format";
import { useMe } from "../lib/hooks";
import { CreateKeyModal, RevealKeyModal } from "./keys";

const DIRECTION_LABEL = { put: "Upload", get: "Download", both: "Upload + download" } as const;
const KEY_TONE = { active: "ok", expired: "warn", revoked: "danger" } as const;

export function DestinationDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const destination = useQuery({
    queryKey: ["destination", id],
    queryFn: () => api.destinations.get(id),
  });
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.connectors.list(),
  });

  const remove = useMutation({
    mutationFn: () => api.destinations.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["destinations"] });
      navigate("/destinations");
    },
  });

  if (destination.isLoading) return <Loading />;
  if (destination.isError || !destination.data)
    return <ErrorState error={destination.error} onRetry={destination.refetch} />;

  const d = destination.data;
  const connector = connectors.data?.find((c) => c.id === d.connectorId);

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 text-sm text-ink-subtle">
          <button onClick={() => navigate("/destinations")} className="hover:text-ink">
            Destinations
          </button>{" "}
          ›{" "}
          {connector ? (
            <button
              onClick={() => navigate(`/connectors/${connector.id}`)}
              className="hover:text-ink"
            >
              {connector.displayName}
            </button>
          ) : (
            "—"
          )}{" "}
          › <span className="text-ink mono">{d.bucketOrContainer}</span>
        </div>
        <div className="flex items-center gap-3">
          <h1 className="mono text-xl font-semibold text-ink">{d.bucketOrContainer}</h1>
          <StatusBadge health={d.status} />
        </div>
      </div>

      <SettingsCard key={d.id} dest={d} onSaved={() => destination.refetch()} />

      <KeysCard destination={d} />

      {/* Danger zone */}
      <div className="card border-danger/30 p-5">
        <h2 className="text-sm font-semibold text-ink">Danger zone</h2>
        <div className="mt-2 flex items-center justify-between gap-4">
          <p className="text-sm text-ink-muted">
            Deleting this destination revokes every key issued against it. The
            bucket/container itself is untouched. This can't be undone.
          </p>
          <button
            className="btn-danger shrink-0"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`Delete destination "${d.bucketOrContainer}"? Its keys will be revoked.`))
                remove.mutate();
            }}
          >
            {remove.isPending ? "Deleting…" : "Delete destination"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Editable destination settings, PATCHed on save. Mounted once data is ready
 *  (keyed by id), so initial state comes straight from the row. */
function SettingsCard({ dest, onSaved }: { dest: Destination; onSaved: () => void }) {
  const qc = useQueryClient();
  const [keyPrefix, setKeyPrefix] = useState(dest.keyPrefix);
  const [naming, setNaming] = useState<NamingMode>(dest.naming);
  const [isolateByUser, setIsolateByUser] = useState(dest.isolateByUser);
  const [maxMb, setMaxMb] = useState(
    dest.maxBytes != null ? String(Math.round(dest.maxBytes / (1024 * 1024))) : "",
  );

  const save = useMutation({
    mutationFn: (input: UpdateDestinationInput) => api.destinations.update(dest.id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["destinations"] });
      onSaved();
    },
  });

  const currentMb =
    dest.maxBytes != null ? String(Math.round(dest.maxBytes / (1024 * 1024))) : "";
  const dirty =
    keyPrefix !== dest.keyPrefix ||
    naming !== dest.naming ||
    isolateByUser !== dest.isolateByUser ||
    maxMb !== currentMb;

  function submit() {
    save.mutate({
      keyPrefix,
      naming,
      isolateByUser,
      maxBytes: maxMb ? Number(maxMb) * 1024 * 1024 : null,
    });
  }

  return (
    <div className="card p-5">
      <h2 className="mb-4 text-sm font-semibold text-ink">Settings</h2>
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="d-prefix">
            Key prefix <span className="text-ink-subtle">(optional)</span>
          </label>
          <input
            id="d-prefix"
            className="input mono"
            placeholder="incoming/2026/"
            value={keyPrefix}
            onChange={(e) => setKeyPrefix(e.target.value)}
          />
        </div>

        <div>
          <span className="label">Object naming</span>
          <NamingSelect value={naming} onChange={setNaming} />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-surface-300 text-accent focus:ring-accent/30"
            checked={isolateByUser}
            onChange={(e) => setIsolateByUser(e.target.checked)}
          />
          Keep each person's files private (isolate by owner prefix)
        </label>

        <p className="text-xs text-ink-subtle">
          Who can use this destination is controlled on its{" "}
          <span className="text-ink">connection</span> (access &amp; assigned
          members) — open it from the breadcrumb above.
        </p>

        <div>
          <label className="label" htmlFor="d-maxmb">
            Max object size (MB) <span className="text-ink-subtle">(optional)</span>
          </label>
          <input
            id="d-maxmb"
            type="number"
            min="1"
            className="input max-w-[200px]"
            placeholder="500"
            value={maxMb}
            onChange={(e) => setMaxMb(e.target.value)}
          />
        </div>

        {save.isError && (
          <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {save.error instanceof Error ? save.error.message : "Could not save."}
          </p>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            className="btn-primary"
            disabled={!dirty || save.isPending}
            onClick={submit}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
          {save.isSuccess && !dirty && (
            <span className="text-sm text-ok">✓ Saved</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Keys issued against this destination: who has access + revoke + new key. */
function KeysCard({ destination }: { destination: Destination }) {
  const qc = useQueryClient();
  const me = useMe();
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreateKeyResult | null>(null);

  const keys = useQuery({ queryKey: ["keys"], queryFn: () => api.keys.list() });
  const revoke = useMutation({
    mutationFn: (kid: string) => api.keys.remove(kid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });

  const mine = (keys.data ?? []).filter((k) => k.destinationId === destination.id);

  return (
    <div className="card p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">
          Your keys <span className="text-ink-subtle">({mine.length})</span>
        </h2>
        <button className="btn-secondary" onClick={() => setCreating(true)}>
          New key
        </button>
      </div>
      <p className="mb-4 text-xs text-ink-subtle">
        Keys are personal — each teammate creates and sees only their own. Use the
        Activity log for org-wide oversight of who sent what.
      </p>

      {mine.length > 0 ? (
        <Table>
          <THead>
            <tr>
              <TH>Key</TH>
              <TH>Direction</TH>
              <TH>Usage</TH>
              <TH>Last used</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </tr>
          </THead>
          <TBody>
            {mine.map((k) => (
              <TR key={k.id}>
                <TD className="font-medium text-ink">{k.label ?? "Untitled key"}</TD>
                <TD>
                  <OwnerCell email={k.ownerEmail} youEmail={me.data?.user.email} />
                </TD>
                <TD className="text-ink-muted">{DIRECTION_LABEL[k.direction]}</TD>
                <TD className="text-xs text-ink-muted">
                  {k.usedCount} used
                  {k.monthlyQuota != null ? ` / ${k.monthlyQuota}/mo` : ""}
                  {k.expiresAt ? ` · expires ${formatRelative(k.expiresAt)}` : ""}
                </TD>
                <TD className="text-ink-muted">{formatRelative(k.lastUsedAt)}</TD>
                <TD>
                  <Badge tone={KEY_TONE[k.status]}>{k.status}</Badge>
                </TD>
                <TD className="text-right">
                  <button
                    className="btn-danger"
                    disabled={k.status !== "active" || revoke.isPending}
                    onClick={() => {
                      if (confirm(`Revoke key "${k.label ?? "Untitled key"}"?`))
                        revoke.mutate(k.id);
                    }}
                  >
                    Revoke
                  </button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <p className="text-sm text-ink-muted">
          No keys issued here yet. Create one and hand the one-liner to whoever
          needs to send files.
        </p>
      )}

      <CreateKeyModal
        open={creating}
        onClose={() => setCreating(false)}
        destinations={[destination]}
        fixedDestinationId={destination.id}
        onCreated={(result) => {
          setCreating(false);
          setRevealed(result);
        }}
      />
      <RevealKeyModal result={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}
